import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as entityService from '../../services/entityManagementService'; // Import the service
import { calculateUserStreak } from '../../utils/goalUtils'; // Import streak calculation
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import Page from '../../components/ui/Page';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';

// Import all components
import MilestoneCard from './components/MilestoneCard';
import DateHeader from './components/DateHeader';
import GoalSelector from './components/GoalSelector';
import ProgressVisualization from './components/ProgressVisualization';
import TimelineProgressVisualization from './components/TimelineProgressVisualization';
import EnhancedTimelineProgressVisualization from './components/EnhancedTimelineProgressVisualization';
import AddMilestoneModal from './components/AddMilestoneModal';
import AIAssistantPanel from './components/AIAssistantPanel';
import ProgressAIAssistant from './components/ProgressAIAssistant';
import ReflectionPrompt from './components/ReflectionPrompt';
import aiProgressJourneyService from '../../services/aiProgressJourneyService';

const Progress = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [viewMode, setViewMode] = useState('day');
  const [milestones, setMilestones] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const [isAIExpanded, setIsAIExpanded] = useState(false);
  const [showReflectionPrompt, setShowReflectionPrompt] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [error, setError] = useState(null);
  const [aiJourney, setAiJourney] = useState([]);
  const [useAIJourney, setUseAIJourney] = useState(true);

  // Load goals using entityService instead of mock data
  const [goals, setGoals] = useState([]);

  // Load goals using the entity service
  useEffect(() => {
    if (isAuthenticated && user) {
      (async () => {
        try {
          const userGoals = await entityService.getGoals(user);
          setGoals(Array.isArray(userGoals) ? userGoals : []);
        } catch (e) {
          setGoals([]);
          setError('Failed to load goals. Please check your connection or permissions.');
        }
      })();
    } else {
      setGoals([]);
    }
  }, [isAuthenticated, user]);

  const getMilestoneStorageKey = useCallback(() => {
    if (user && user.id) {
      return `milestones_data_${user.id}`;
    }
    // Return a default or null if user or user.id is not available,
    // or handle this case as appropriate for your application logic.
    return null;
  }, [user]);

  // Load milestones using the entity service
  useEffect(() => {
    if (isAuthenticated && user) {
      try {
        const userMilestones = entityService.getMilestones(user);
        setMilestones(Array.isArray(userMilestones) ? userMilestones : []);
      } catch (e) {
        setMilestones([]);
        setError('Failed to load milestones. Please check your connection or permissions.');
      }
    } else {
      setMilestones([]);
    }
  }, [isAuthenticated, user, selectedDate]); // Reload if selectedDate changes, to fetch relevant milestones


  // Check for evening reflection prompt. This useEffect remains the same.
  useEffect(() => {
    const checkReflectionTime = () => {
      const now = new Date();
      const hour = now.getHours();
      
      // Show reflection prompt after 8 PM if there are completed milestones
      if (hour >= 20 && getFilteredMilestones().some(m => m.completed)) {
        const today = new Date().toISOString().split('T')[0];
        const hasReflectedToday = localStorage.getItem(`reflection_${today}`);
        
        if (!hasReflectedToday) {
          setShowReflectionPrompt(true);
        }
      }
    };

    checkReflectionTime();
    const interval = setInterval(checkReflectionTime, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [milestones]);

  const getFilteredMilestones = () => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    let filtered = Array.isArray(milestones) ? milestones.filter(milestone => milestone.date === dateStr) : [];
    
    if (selectedGoalId) {
      filtered = filtered.filter(milestone => milestone.goalId === selectedGoalId);
    }
    
    return filtered.sort((a, b) => {
      // Sort by completion status, then priority, then due time
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      
      if (a.dueTime && b.dueTime) {
        return a.dueTime.localeCompare(b.dueTime);
      }
      
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  };

  // Determine which progress data to use
  const getActiveProgressData = () => {
    if (useAIJourney && aiJourney.length > 0) {
      return aiJourney.filter(milestone => selectedGoalId ? milestone.goalId === selectedGoalId : true);
    }
    return getFilteredMilestones();
  };

  const handleToggleComplete = async (milestoneId) => {
    if (user) {
      const milestone = milestones.find(m => m.id === milestoneId);
      if (milestone) {
        const updatedMilestone = entityService.updateMilestone(user, milestoneId, { completed: !milestone.completed });
        if (updatedMilestone) {
          setMilestones(prevMilestones =>
            prevMilestones.map(m =>
              m.id === milestoneId ? { ...updatedMilestone, createdAt: new Date(updatedMilestone.createdAt) } : m
            )
          );
        }
      }
    }
  };

  const handleStartFocus = (milestone) => {
    // Navigate to focus mode with milestone context
    window.location.href = `/focus-mode?milestoneId=${milestone.id}&goalId=${milestone.goalId}`;
  };

  const handleEditMilestone = (milestoneDataFromModal) => {
    if (user && milestoneDataFromModal && milestoneDataFromModal.id) {
      const updatedMilestone = entityService.updateMilestone(user, milestoneDataFromModal.id, milestoneDataFromModal);
      if (updatedMilestone) {
        setMilestones(prevMilestones =>
          prevMilestones.map(m =>
            m.id === updatedMilestone.id ? { ...updatedMilestone, createdAt: new Date(updatedMilestone.createdAt) } : m
          )
        );
      }
    }
    setEditingMilestone(null);
    setIsAddModalOpen(false);
  };

  const handleDeleteMilestone = (milestoneId) => {
    if (user) {
      const success = entityService.deleteMilestone(user, milestoneId);
      if (success) {
        setMilestones(prevMilestones => prevMilestones.filter(milestone => milestone.id !== milestoneId));
      }
    }
  };

  const handleAddMilestone = (newMilestoneData) => { // newMilestoneData from AddMilestoneModal
    if (user) {
      const goal = goals.find(g => g.id === newMilestoneData.goalId);
      // Ensure 'date' is included, typically from selectedDate
      const milestonePayload = {
        ...newMilestoneData,
        date: newMilestoneData.date || selectedDate.toISOString().split('T')[0], // Ensure date is set
        goalName: goal ? goal.title : 'N/A', // Add goalName if not provided by modal
        // completed and createdAt will be set by the service or are defaults
      };
      const createdMilestone = entityService.createMilestone(user, milestonePayload);
      if (createdMilestone) {
        setMilestones(prevMilestones => [...prevMilestones, { ...createdMilestone, createdAt: new Date(createdMilestone.createdAt) }]);
      }
    }
  };

  const handleCreateProgressPath = (pathMilestones) => {
    if (user && selectedGoalId && Array.isArray(pathMilestones)) {
      const goal = goals.find(g => g.id === selectedGoalId);
      
      pathMilestones.forEach((milestone, index) => {
        // Calculate dates for milestones, spacing them out over time
        const baseDate = new Date(selectedDate);
        baseDate.setDate(baseDate.getDate() + (index * (milestone.estimatedDays || 7)));
        
        const milestonePayload = {
          title: milestone.title,
          description: milestone.description || milestone.successCriteria || '',
          goalId: selectedGoalId,
          goalName: goal ? goal.title : 'N/A',
          priority: milestone.priority || 'medium',
          date: baseDate.toISOString().split('T')[0],
          completed: false,
          createdAt: new Date(),
          aiGenerated: true // Mark as AI generated
        };
        
        const createdMilestone = entityService.createMilestone(user, milestonePayload);
        if (createdMilestone) {
          setMilestones(prevMilestones => [...prevMilestones, { ...createdMilestone, createdAt: new Date(createdMilestone.createdAt) }]);
        }
      });
    }
  };

  const handleCreateAIJourney = (journey) => {
    setAiJourney(journey);
    // Optionally save to milestones storage
    if (user && journey.length > 0) {
      journey.forEach(milestone => {
        const milestonePayload = {
          ...milestone,
          date: selectedDate.toISOString().split('T')[0],
          createdAt: new Date()
        };
        
        const createdMilestone = entityService.createMilestone(user, milestonePayload);
        if (createdMilestone) {
          setMilestones(prevMilestones => [...prevMilestones, { ...createdMilestone, createdAt: new Date(createdMilestone.createdAt) }]);
        }
      });
    }
  };

  const handleUpdateAIProgress = (milestoneId, updates) => {
    setAiJourney(prevJourney => 
      prevJourney.map(milestone => 
        milestone.id === milestoneId ? { ...milestone, ...updates } : milestone
      )
    );
    
    // Also update in regular milestones if stored there
    if (user) {
      const updatedMilestone = entityService.updateMilestone(user, milestoneId, updates);
      if (updatedMilestone) {
        setMilestones(prevMilestones =>
          prevMilestones.map(m =>
            m.id === milestoneId ? { ...updatedMilestone, createdAt: new Date(updatedMilestone.createdAt) } : m
          )
        );
      }
    }
  };

  const handleOpenEditModal = (milestone) => {
    setEditingMilestone(milestone);
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingMilestone(null);
  };

  const handleSaveReflection = (reflectionData) => {
    // Save reflection to localStorage
    localStorage.setItem(`reflection_${reflectionData.date}`, JSON.stringify(reflectionData));
    console.log('Reflection saved:', reflectionData);
  };

  const filteredMilestones = getFilteredMilestones();
  const activeProgressData = getActiveProgressData();
  const completedCount = Array.isArray(activeProgressData) ? activeProgressData.filter(m => m.completed).length : 0;
  const totalCount = Array.isArray(activeProgressData) ? activeProgressData.length : 0;
  const selectedGoal = Array.isArray(goals) ? goals.find(goal => goal.id === selectedGoalId) : null;

  // Calculate real weekly progress and streak
  const weeklyProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const streakData = user ? calculateUserStreak(user.id) : { currentStreak: 0 };
  const streakCount = streakData.currentStreak;

  // Defensive: ensure milestones, goals, and activeProgressData are always arrays
  const safeMilestones = Array.isArray(milestones) ? milestones : [];
  const safeGoals = Array.isArray(goals) ? goals : [];
  const safeFilteredMilestones = Array.isArray(filteredMilestones) ? filteredMilestones : [];
  const safeActiveProgressData = Array.isArray(activeProgressData) ? activeProgressData : [];

  return (
    <Page>
      <PageHeader
        icon="TrendingUp"
        title="Progress"
        subtitle="Track milestones and visualize your journey toward each goal"
      />
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-6">
              {/* Date Header */}
              <DateHeader
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onViewModeChange={setViewMode}
                viewMode={viewMode}
              />

              {/* Goal Selector */}
              <GoalSelector
                goals={safeGoals}
                selectedGoalId={selectedGoalId}
                onGoalSelect={setSelectedGoalId}
              />

              {/* Enhanced Timeline Progress Visualization */}
              <EnhancedTimelineProgressVisualization
                selectedGoal={selectedGoal}
                progressMarks={safeActiveProgressData}
                onUpdateProgress={handleUpdateAIProgress}
                onCreateAIJourney={handleCreateAIJourney}
              />

              {/* Toggle between AI and Manual Mode */}
              <div className="flex items-center justify-between p-4 bg-surface rounded-xl border border-border">
                <div className="flex items-center space-x-3">
                  <Icon name="Bot" size={20} className="text-primary" />
                  <div>
                    <div className="text-sm font-medium text-text-primary">AI Progress Mode</div>
                    <div className="text-xs text-text-secondary">
                      {useAIJourney ? 'Using AI-generated milestones' : 'Using manual milestones'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setUseAIJourney(!useAIJourney)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    useAIJourney ? 'bg-primary' : 'bg-surface-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useAIJourney ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Milestones List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-heading-medium text-text-primary">
                    {useAIJourney ? 'AI Progress Journey' : 'Manual Progress Steps'}
                  </h2>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary">
                      {completedCount}/{totalCount} completed
                    </span>
                    {!useAIJourney && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsAddModalOpen(true)}
                        iconName="Plus"
                        className="text-text-secondary hover:text-primary"
                      >
                        Add Step
                      </Button>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-error/10 border border-error/20 rounded text-error text-center mb-4">{error}</div>
                )}

                {safeActiveProgressData.length === 0 ? (
                  <EmptyState
                    icon="Target"
                    title={useAIJourney ? 'Generate Your AI Progress Journey' : 'Start Your Progress Journey'}
                    description={useAIJourney
                      ? 'Let AI analyze your patterns and create personalized milestones.'
                      : 'Create progression marks to track your journey toward your goals.'}
                    action={(
                      <Button
                        variant="primary"
                        onClick={() => useAIJourney ? null : setIsAddModalOpen(true)}
                        iconName={useAIJourney ? 'Bot' : 'Plus'}
                        iconPosition="left"
                      >
                        {useAIJourney ? 'AI will auto-generate when you select a goal' : 'Add Progress Step'}
                      </Button>
                    )}
                    size="md"
                  />
                ) : (
                  <div className="space-y-3">
                    {safeActiveProgressData.map((milestone) => (
                      <MilestoneCard
                        key={milestone.id}
                        milestone={milestone}
                        onToggleComplete={useAIJourney ? 
                          (id) => handleUpdateAIProgress(id, { completed: !milestone.completed }) :
                          handleToggleComplete
                        }
                        onStartFocus={handleStartFocus}
                        onEdit={handleOpenEditModal}
                        onDelete={handleDeleteMilestone}
                        isDragging={draggedItem === milestone.id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <Card padding="md">
                <h3 className="font-heading-medium text-text-primary mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Link
                    to="/focus-mode"
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-surface-700/40 transition-colors duration-fast"
                  >
                    <Icon name="Focus" size={20} className="text-primary mb-2" />
                    <span className="text-xs font-caption text-text-secondary">Focus Mode</span>
                  </Link>
                  
                  <Link
                    to="/goals-dashboard"
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-surface-700/40 transition-colors duration-fast"
                  >
                    <Icon name="Target" size={20} className="text-accent mb-2" />
                    <span className="text-xs font-caption text-text-secondary">Goals</span>
                  </Link>
                  
                  <button
                    onClick={() => setIsAIExpanded(true)}
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-surface-700/40 transition-colors duration-fast"
                  >
                    <Icon name="Bot" size={20} className="text-secondary mb-2" />
                    <span className="text-xs font-caption text-text-secondary">
                      {useAIJourney ? 'AI Journey' : 'Progress AI'}
                    </span>
                  </button>
                  
                  <Link
                    to="/settings-configuration"
                    className="flex flex-col items-center p-3 rounded-lg hover:bg-surface-700/40 transition-colors duration-fast"
                  >
                    <Icon name="Settings" size={20} className="text-warning mb-2" />
                    <span className="text-xs font-caption text-text-secondary">Settings</span>
                  </Link>
                </div>
              </Card>
        </div>

        {/* Progress AI Assistant Panel - Desktop */}
        <div className="hidden lg:block">
          <ProgressAIAssistant
            isExpanded={isAIExpanded}
            onToggle={() => setIsAIExpanded(!isAIExpanded)}
            selectedGoal={selectedGoal}
            progressMarks={safeActiveProgressData}
            onCreateProgressPath={handleCreateProgressPath}
          />
        </div>
      </div>

      {/* Mobile Progress AI Assistant Panel */}
      <div className="lg:hidden">
        <ProgressAIAssistant
          isExpanded={isAIExpanded}
          onToggle={() => setIsAIExpanded(!isAIExpanded)}
          selectedGoal={selectedGoal}
          progressMarks={safeActiveProgressData}
          onCreateProgressPath={handleCreateProgressPath}
        />
      </div>

      {/* Floating Action Button */}
      <FloatingActionButton 
        goalContext={selectedGoal?.title}
      />

      {/* Add Progress Step Modal */}
      <AddMilestoneModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onAdd={handleAddMilestone}
        onEdit={handleEditMilestone}
        goals={safeGoals}
        editingMilestone={editingMilestone}
      />

      {/* Reflection Prompt */}
      <ReflectionPrompt
        isVisible={showReflectionPrompt}
        onClose={() => setShowReflectionPrompt(false)}
        completedMilestones={completedCount}
        totalMilestones={totalCount}
        onSaveReflection={handleSaveReflection}
      />
    </Page>
  );
};

export default Progress;